<script setup lang="ts">
import type { RevalidationFilterState, RevalidationsSortOption } from './revalidationsShared.types'
import { SolCheckbox, SolMenu, SolMenuItem, SolTooltip } from '@solfacil/girassol'
import IconExpandMore from '~icons/material-symbols/expand-more'
import IconSort from '~icons/material-symbols/sort'
import { RevalidationStatus } from '~/services-v2/financial-bff/__generated__/zeus'
import { formatBrandDisplay } from './revalidations.constants'

defineProps<{
  state: RevalidationFilterState
  sortOptions: RevalidationsSortOption[]
  statusLabels: Record<RevalidationStatus, string>
}>()

const emit = defineEmits<{
  toggleSelectAll: []
  selectAll: []
  selectFilled: []
  selectNone: []
  applySort: [option: RevalidationsSortOption]
  toggleBrand: [brand: string]
  toggleStatus: [status: RevalidationStatus]
}>()

function handleSelectAllAndClose(close: () => void) {
  emit('selectAll')
  close()
}

function handleSelectFilledAndClose(close: () => void) {
  emit('selectFilled')
  close()
}

function handleSelectNoneAndClose(close: () => void) {
  emit('selectNone')
  close()
}

function handleApplySortAndClose(close: () => void, option: RevalidationsSortOption) {
  emit('applySort', option)
  close()
}
</script>

<template>
  <div class="session-filters">
    <div class="filters-left">
      <SolTooltip
        v-if="state.batchSelectionDisabled"
        :text="state.batchSelectionDisabledTooltip"
        position="top"
      >
        <span class="select-all-wrapper select-all-wrapper--batch-locked">
          <SolCheckbox
            id="revalidation-filter-select-all"
            name="revalidation-filter-select-all"
            value="all"
            :checked="false"
            :indeterminate="false"
            disabled
          />
          <button
            type="button"
            class="select-all-arrow"
            disabled
            aria-hidden="true"
          >
            <IconExpandMore class="select-all-arrow-icon" aria-hidden="true" />
          </button>
        </span>
      </SolTooltip>
      <div v-else class="select-all-wrapper">
        <SolCheckbox
          id="revalidation-filter-select-all"
          name="revalidation-filter-select-all"
          value="all"
          :checked="state.allSelected"
          :indeterminate="state.someSelected"
          @change="emit('toggleSelectAll')"
        />
        <SolMenu id="revalidation-select-all-menu" position="bottom">
          <template #trigger="{ arias, toggle, id: triggerId }">
            <button
              :id="triggerId"
              type="button"
              class="select-all-arrow"
              :aria-expanded="arias.ariaExpanded"
              :aria-haspopup="arias.ariaHaspopup"
              :class="arias.class"
              @click="toggle"
            >
              <IconExpandMore class="select-all-arrow-icon" aria-hidden="true" />
            </button>
          </template>
          <template #default="{ close }">
            <SolMenuItem id="revalidation-select-all" @clicked="handleSelectAllAndClose(close)">
              Todas
            </SolMenuItem>
            <SolMenuItem id="revalidation-select-filled" @clicked="handleSelectFilledAndClose(close)">
              Somente as preenchidas
            </SolMenuItem>
            <SolMenuItem id="revalidation-select-none" @clicked="handleSelectNoneAndClose(close)">
              Nenhuma
            </SolMenuItem>
          </template>
        </SolMenu>
      </div>

      <SolMenu id="revalidation-sort-menu" position="bottom" class="sort-wrapper">
        <template #trigger="{ arias, toggle, id: triggerId }">
          <button
            :id="triggerId"
            type="button"
            class="filters-sort"
            :aria-expanded="arias.ariaExpanded"
            :aria-haspopup="arias.ariaHaspopup"
            :class="[arias.class, { 'filters-sort--open': arias.ariaExpanded }]"
            @click="toggle"
          >
            <IconSort class="filters-sort-type-icon" aria-hidden="true" />
            <span class="sort-label">{{ state.activeSortLabel }}</span>
            <IconExpandMore
              class="sort-arrow"
              :class="{ 'sort-arrow--open': arias.ariaExpanded }"
              aria-hidden="true"
            />
          </button>
        </template>
        <template #default="{ close }">
          <SolMenuItem
            v-for="option in sortOptions"
            :id="`sort-option-${option.order_by}-${option.order_dir}`"
            :key="option.label"
            :class="{ 'sort-option--active': option.order_by === state.sortBy && option.order_dir === state.sortDir }"
            @clicked="handleApplySortAndClose(close, option)"
          >
            {{ option.label }}
          </SolMenuItem>
        </template>
      </SolMenu>
    </div>

    <div class="filters-right">
      <span class="filters-label">Filtrar:</span>
      <template v-if="state.isSummaryPending">
        <div v-for="n in 4" :key="n" class="filter-chip-skeleton" />
      </template>
      <template v-else-if="state.hasSummary">
        <button
          v-for="brand in state.sortedBrands"
          :key="brand.brand"
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': state.selectedBrands.has(brand.brand) }"
          @click="emit('toggleBrand', brand.brand)"
        >
          <span class="chip-label">{{ formatBrandDisplay(brand.brand) }}</span>
          <span class="chip-count">{{ brand.total }}</span>
        </button>

        <button
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': state.selectedStatuses.has(RevalidationStatus.PENDING_SUBMISSION) && state.selectedBrands.size === 0 }"
          @click="emit('toggleStatus', RevalidationStatus.PENDING_SUBMISSION)"
        >
          <span class="chip-label">{{ statusLabels[RevalidationStatus.PENDING_SUBMISSION] }}</span>
          <span class="chip-count">{{ state.summaryStatusCounts[RevalidationStatus.PENDING_SUBMISSION] }}</span>
        </button>

        <button
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': state.selectedStatuses.has(RevalidationStatus.INVALID_DATA) && state.selectedBrands.size === 0 }"
          @click="emit('toggleStatus', RevalidationStatus.INVALID_DATA)"
        >
          <span class="chip-label">{{ statusLabels[RevalidationStatus.INVALID_DATA] }}</span>
          <span class="chip-count">{{ state.summaryStatusCounts[RevalidationStatus.INVALID_DATA] }}</span>
        </button>

        <button
          v-if="state.summaryStatusCounts[RevalidationStatus.WAITING_COMMISSIONING_RESULT] > 0"
          type="button"
          class="filter-chip filter-chip--analysis"
          :class="{ 'filter-chip--active': state.selectedStatuses.has(RevalidationStatus.WAITING_COMMISSIONING_RESULT) && state.selectedBrands.size === 0 }"
          @click="emit('toggleStatus', RevalidationStatus.WAITING_COMMISSIONING_RESULT)"
        >
          <span class="chip-label">{{ statusLabels[RevalidationStatus.WAITING_COMMISSIONING_RESULT] }}</span>
          <span class="chip-count">{{ state.summaryStatusCounts[RevalidationStatus.WAITING_COMMISSIONING_RESULT] }}</span>
        </button>

        <button
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': state.selectedStatuses.has(RevalidationStatus.APPROVED) && state.selectedBrands.size === 0 }"
          @click="emit('toggleStatus', RevalidationStatus.APPROVED)"
        >
          <span class="chip-label">{{ statusLabels[RevalidationStatus.APPROVED] }}</span>
          <span class="chip-count">{{ state.summaryStatusCounts[RevalidationStatus.APPROVED] }}</span>
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.session-filters {
  @apply flex flex-col gap-3;

  @screen md:system {
    @apply flex-row items-center justify-between;
  }

  > .filters-left {
    @apply flex flex-row items-center gap-3;

    > .select-all-wrapper {
      @apply flex flex-row items-center;

      &--batch-locked {
        @apply cursor-not-allowed opacity-50;
      }

      > .select-all-arrow {
        @apply flex items-center justify-center;
        @apply w-5 h-5 ml-1;
        @apply rounded-sm;
        @apply bg-transparent border-0;
        @apply cursor-pointer;
        @apply text-neutral-low-medium;

        > .select-all-arrow-icon {
          @apply w-3 h-3 flex-shrink-0;
        }
      }
    }

    > .sort-wrapper {
      @apply flex-1;

      @screen md:system {
        @apply flex-none;
      }

      > .filters-sort {
        @apply flex items-center gap-2;
        @apply w-full self-stretch;
        @apply px-3 py-[6px];
        @apply rounded-md border-a-xs border-neutral-high-medium;
        @apply bg-neutral-high-pure;
        @apply cursor-pointer;

        @screen md:system {
          @apply w-[208.578px];
        }

        &--open {
          @apply border-brand-primary-pure;
        }

        > .filters-sort-type-icon {
          @apply w-4 h-4 flex-shrink-0 text-neutral-low-medium;
        }

        > .sort-label {
          @apply fonts-body-small-regular text-neutral-low-medium;
          @apply flex-1 text-left;
        }

        > .sort-arrow {
          @apply w-3 h-3 flex-shrink-0 text-neutral-low-medium;
          @apply transition-transform duration-150;

          &--open {
            @apply rotate-180;
          }
        }
      }

      :deep(.sort-option--active) {
        @apply fonts-body-medium-bold text-brand-primary-pure;
      }
    }
  }

  > .filters-right {
    @apply flex flex-row items-center gap-2;
    @apply overflow-x-auto;
    -webkit-overflow-scrolling: touch;

    @screen md:system {
      @apply overflow-x-visible flex-wrap;
    }

    > .filters-label {
      @apply fonts-body-medium-bold;
      @apply text-neutral-low-medium;
      @apply whitespace-nowrap;
      @apply flex-shrink-0;
    }

    > .filter-chip-skeleton {
      height: 32px;
      width: 90px;
      border-radius: 100px;
      flex-shrink: 0;
      @apply relative overflow-hidden;
      background-color: #e4e4e4;

      &::after {
        @apply absolute inset-0 w-full h-full;
        content: '';
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.4) 50%,
          rgba(255, 255, 255, 0) 100%
        );
        animation: shimmer 1.5s infinite;
      }
    }

    > .filter-chip {
      @apply flex items-center justify-center;
      @apply h-xs px-3 py-2;
      @apply gap-nano;
      @apply rounded-full;
      @apply bg-neutral-high-light;
      @apply border-a-xs border-transparent;
      @apply cursor-pointer;
      @apply whitespace-nowrap;

      &--active {
        @apply bg-transparent;
        @apply border-brand-primary-pure;

        > .chip-label {
          @apply text-brand-primary-pure;
        }

        > .chip-count {
          @apply bg-brand-primary-pure;
        }
      }

      &--analysis {
        border: 4px solid #D1E4F6;
        @apply bg-neutral-high-light;

        &.filter-chip--active {
          border-color: #D1E4F6;
          @apply bg-neutral-high-light;

          > .chip-label {
            @apply text-neutral-low-medium;
          }
        }
      }

      > .chip-label {
        @apply fonts-body-medium-regular;
        @apply text-neutral-low-medium;
      }

      > .chip-count {
        @apply flex items-center justify-center;
        @apply min-w-5 h-5 px-[5px] py-[2px];
        @apply rounded-full;
        @apply bg-neutral-low-pure text-neutral-high-pure;
        @apply text-center;
        @apply fonts-body-small-bold;
      }

      &--analysis > .chip-count {
        border-radius: 100px;
        @apply bg-feedback-informative-pure text-neutral-high-pure;
      }
    }
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
